/**
 * ErrorBoundary Component
 *
 * Catches React rendering errors and reports them to the backend.
 * Displays a user-friendly error message instead of crashing the app.
 */
import React, { Component, ErrorInfo, ReactNode } from 'react'
import { errorReportingService } from '../../services/errorReporting.service'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Report the error to our backend
        errorReportingService.reportReactError(error, errorInfo.componentStack || '')

        // Also log to console for development
        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    handleRefresh = (): void => {
        window.location.reload()
    }

    handleGoHome = (): void => {
        window.location.href = '/'
    }

    render(): ReactNode {
        if (this.state.hasError) {
            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Default error UI
            return (
                <div style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#0a0a0a',
                    padding: '20px'
                }}>
                    <div style={{
                        maxWidth: '500px',
                        textAlign: 'center',
                        padding: '40px',
                        borderRadius: '24px',
                        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
                        border: '1px solid rgba(239, 68, 68, 0.3)'
                    }}>
                        <div style={{
                            fontSize: '64px',
                            marginBottom: '20px'
                        }}>
                            ⚠️
                        </div>
                        <h1 style={{
                            color: '#ffffff',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            marginBottom: '16px'
                        }}>
                            Something went wrong
                        </h1>
                        <p style={{
                            color: '#d1d5db',
                            fontSize: '16px',
                            lineHeight: '1.6',
                            marginBottom: '24px'
                        }}>
                            We've been notified and are working to fix the issue.
                            Please try refreshing the page or go back to the homepage.
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={this.handleRefresh}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#ffffff',
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                                    border: 'none',
                                    borderRadius: '50px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)'
                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(139, 92, 246, 0.4)'
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)'
                                    e.currentTarget.style.boxShadow = 'none'
                                }}
                            >
                                Refresh Page
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#d1d5db',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '50px',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                                }}
                            >
                                Go to Homepage
                            </button>
                        </div>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{
                                marginTop: '24px',
                                textAlign: 'left',
                                padding: '16px',
                                background: 'rgba(0, 0, 0, 0.3)',
                                borderRadius: '12px'
                            }}>
                                <summary style={{
                                    color: '#f87171',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}>
                                    Error Details (Development Only)
                                </summary>
                                <pre style={{
                                    color: '#fca5a5',
                                    fontSize: '12px',
                                    marginTop: '12px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'monospace'
                                }}>
                                    {this.state.error.toString()}
                                    {'\n\n'}
                                    {this.state.error.stack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
